import React, {useEffect, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant, FormItem,
  InputType,
  Label, Loading, MarkdownView,
  ParagraphSize, useModal,
  useToast
} from '@fs/uikit-react';
import styles from './NoteModal.module.css';
import {Note, NoteApi, NoteType} from "../../../../../api/NoteApi.ts";
import {ApiError} from "@/services/api";

interface NoteModalProps {
  personId: string;
  noteId?: string;
  onSuccess: () => void;
}

const NoteModal: React.FC<NoteModalProps> = ({personId, noteId, onSuccess}) => {
  const {closeModal} = useModal();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (noteId) {
      const fetchNote = async () => {
        setIsFetching(true);
        try {
          const response = await NoteApi.getById(noteId);
          const note = response.data;
          setTitle(note.title);
          setBody(note.body);
        } catch (error) {
          console.error('Error fetching note:', error);
          toast.showErrorToast(
            'Помилка завантаження нотатки',
            getUserFriendlyErrorMessage(error as ApiError)
          );
        } finally {
          setIsFetching(false);
        }
      };
      fetchNote();
    }
  }, [noteId, toast]);

  const handleSubmit = async () => {
    if (!title) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, заповніть заголовок');
      return;
    }

    setIsLoading(true);

    const noteData: Note = {
      title,
      body,
      personId,
      type: NoteType.MARKDOWN,
    };

    try {
      if (noteId) {
        await NoteApi.update(noteId, noteData);
        toast.showSuccessToast('Успішно', 'Нотатку успішно оновлено');
      } else {
        await NoteApi.create(noteData);
        toast.showSuccessToast('Успішно', 'Нотатку успішно створено');
      }
      closeModal();
      onSuccess();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.showErrorToast(
        `Помилка збереження нотатки`,
        getUserFriendlyErrorMessage(error as ApiError)
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return <Loading text="Завантаження нотатки..." />;
  }

  return (
    <div className={styles.modalContent}>
      <div className={styles.row}>
        <FormItem
          label="Заголовок"
          type={InputType.TEXT}
          value={title}
          onChange={(e) => { setTitle(e.target.value); }}
        />
      </div>

      <div className={styles.row}>
        <FormItem
          label="Зміст"
          type={InputType.TEXT_AREA}
          value={body}
          onChange={(e) => { setBody(e.target.value); }}
          className={styles.noteBody}
        />
        <div className={styles.markdownPreviewContainer}>
          <div className={styles.previewLabel}>
            <Label
              text="Перегляд"
              paragraphSize={ParagraphSize.SIZE_12}
              className={styles.label}
            />
          </div>
          <div className={styles.markdownPreview}>
            <MarkdownView markdown={body} />
          </div>
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <Button
          title={isLoading ? 'Збереження...' : (noteId ? 'Оновити' : 'Створити')}
          onClick={handleSubmit}
          loading={isLoading}
        />
        <Button
          title="Скасувати"
          onClick={closeModal}
          variant={ButtonVariant.SECONDARY}
        />
      </div>
    </div>
  );
};

export default NoteModal;
